import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { api } from "../lib/api";

/**
 * Warning banner listing usage types that have no Property Tax rate configured
 * for the selected (or current) financial year. Shown to admins before demand
 * generation so incomplete rate setup is never a surprise.
 */
export const MissingRatesBanner = ({ code, fyId, linkToRates = true, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const params = fyId ? { financial_year_id: fyId } : {};
    api.get(`/t/${code}/rate-engine/missing-rates`, { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [code, fyId, refreshKey]);

  if (!data || !data.missing_count) return null;

  const PREVIEW = 8;
  const shown = expanded ? data.missing_usage_types : data.missing_usage_types.slice(0, PREVIEW);
  const hiddenCount = data.missing_count - PREVIEW;

  return (
    <Alert className="border-amber-300 bg-amber-50/70 text-amber-900" data-testid="missing-rates-banner">
      <AlertTriangle className="h-4 w-4 !text-amber-600" />
      <AlertTitle className="font-heading text-sm" data-testid="missing-rates-title">
        Rate Engine setup incomplete — {data.missing_count} item(s) need attention
        {data.fy_label ? ` (FY ${data.fy_label})` : ""}
      </AlertTitle>
      <AlertDescription className="text-amber-800">
        <p className="text-xs mb-2">
          Property tax (ARV) will calculate ₹0 until these are configured in Rate Engine → Property Tax (ARV Matrix).
        </p>
        <div className="flex flex-wrap gap-1" data-testid="missing-rates-list">
          {shown.map((u, i) => (
            <Badge key={u.id || i} variant="outline" className="border-amber-300 bg-white/70 text-amber-900 text-[10px] font-normal">
              {u.usage_type_name}
            </Badge>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          {hiddenCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-amber-800 hover:bg-amber-100 hover:text-amber-900"
              onClick={() => setExpanded((e) => !e)} data-testid="missing-rates-toggle">
              {expanded ? <><ChevronUp className="h-3 w-3 mr-1" /> Show less</> : <><ChevronDown className="h-3 w-3 mr-1" /> Show all {data.missing_count}</>}
            </Button>
          )}
          {linkToRates && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-amber-800 hover:bg-amber-100 hover:text-amber-900" asChild>
              <Link to={`/${code}/admin/rate-engine`} data-testid="missing-rates-link">
                Configure rates <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};
